import React, { useState, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImportExportLang } from '../../translations/imp_lang';
import Modal from './Modal';
import { Button } from './Button';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import {
  parseFile,
  autoMapFields,
  validateRows,
  downloadTemplate,
  FieldMapping,
  ParsedFile,
} from '../../utils/importExportService';
import toast from 'react-hot-toast';

interface ImportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  mappings: FieldMapping[];
  onImport: (rows: Record<string, any>[]) => Promise<{ success: number; failed: number; errors: string[] }>;
  entityName: string;
  templateFilename: string;
}

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'result';

const ImportWizard: React.FC<ImportWizardProps> = ({
  isOpen,
  onClose,
  mappings,
  onImport,
  entityName,
  templateFilename,
}) => {
  const { language } = useLanguage();
  const tr = getImportExportLang(language);
  const [step, setStep] = useState<Step>('upload');
  const [parsedFile, setParsedFile] = useState<ParsedFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validRows, setValidRows] = useState<Record<string, any>[]>([]);
  const [importResult, setImportResult] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setParsedFile(null);
    setFileName('');
    setFieldMapping({});
    setValidationErrors([]);
    setValidRows([]);
    setImportResult(null);
  }, []);

  const handleClose = () => {
    if (step !== 'importing') {
      reset();
      onClose();
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      const parsed = await parseFile(file);
      if (parsed.rows.length === 0) {
        toast.error(tr.import.emptyFile);
        return;
      }
      setParsedFile(parsed);
      setFileName(file.name);
      const autoMapped = autoMapFields(parsed.headers, mappings);
      setFieldMapping(autoMapped);
      setStep('mapping');
    } catch (error) {
      console.error('File parse error:', error);
      toast.error(tr.import.parseError);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleMappingChange = (field: string, sourceColumn: string) => {
    setFieldMapping(prev => ({ ...prev, [field]: sourceColumn }));
  };

  const handleValidate = () => {
    const { valid, errors } = validateRows(parsedFile!.rows, fieldMapping, mappings);
    setValidRows(valid);
    setValidationErrors(errors);
    setStep('preview');
  };

  const handleImport = async () => {
    setStep('importing');
    try {
      const result = await onImport(validRows);
      setImportResult(result);
      setStep('result');
      if (result.success > 0) {
        toast.success(tr.import.importSuccess.replace('{count}', String(result.success)));
      }
      if (result.failed > 0) {
        toast.error(tr.import.importFailed.replace('{count}', String(result.failed)));
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(tr.import.importError);
      setStep('preview');
    }
  };

  const handleDownloadTemplate = () => {
    downloadTemplate(mappings, templateFilename, 'csv');
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'upload', label: tr.import.stepUpload },
    { key: 'mapping', label: tr.import.stepMapping },
    { key: 'preview', label: tr.import.stepPreview },
    { key: 'result', label: tr.import.stepResult },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={tr.import.title.replace('{entity}', entityName)} size="4xl">
      <div className="p-6 space-y-6">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((s, idx) => (
            <React.Fragment key={s.key}>
              <div className="flex items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  idx <= currentStepIndex
                    ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
                }`}>
                  {idx < currentStepIndex ? <CheckCircleIcon className="w-5 h-5" /> : idx + 1}
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:block ${
                  idx <= currentStepIndex ? 'text-gray-900 dark:text-white' : 'text-gray-400'
                }`}>
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 transition-all ${
                  idx < currentStepIndex ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
              >
                <motion.div
                  animate={{ y: isDragging ? -5 : 0 }}
                  className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg"
                >
                  <ArrowDownTrayIcon className="w-8 h-8 text-white" />
                </motion.div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  {tr.import.dropHere}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {tr.import.supportedFormats}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.ods,.txt"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </div>

              <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="w-6 h-6 text-blue-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {tr.import.noTemplate}
                  </span>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                  {tr.import.downloadTemplate}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Mapping */}
          {step === 'mapping' && parsedFile && (
            <motion.div
              key="mapping"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {tr.import.file}: <span className="font-medium text-gray-900 dark:text-white">{fileName}</span> ({parsedFile.rows.length} {tr.import.rows})
                  </p>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {tr.import.targetField}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {tr.import.sourceColumn}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {tr.import.required}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {mappings.map((mapping) => (
                      <tr key={mapping.field} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {mapping.label}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={fieldMapping[mapping.field] || ''}
                            onChange={(e) => handleMappingChange(mapping.field, e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">{tr.import.noMapping}</option>
                            {parsedFile.headers.map((header: string) => (
                              <option key={header} value={header}>{header}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {mapping.required ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              {tr.import.required}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('upload')}>
                  <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
                  {tr.import.back}
                </Button>
                <Button variant="primary" onClick={handleValidate}>
                  {tr.import.next}
                  <ArrowRightIcon className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && parsedFile && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-400">{validRows.length}</p>
                      <p className="text-xs text-green-600 dark:text-green-500">{tr.import.validRows}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
                    <div>
                      <p className="text-2xl font-bold text-red-700 dark:text-red-400">{validationErrors.length}</p>
                      <p className="text-xs text-red-600 dark:text-red-500">{tr.import.errorRows}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-2">
                    <TableCellsIcon className="w-6 h-6 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{parsedFile.rows.length}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-500">{tr.import.totalRows}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="max-h-32 overflow-y-auto bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                  <p className="text-sm font-bold text-red-700 dark:text-red-400 mb-2">
                    {tr.import.errors} ({validationErrors.length}):
                  </p>
                  <ul className="space-y-1">
                    {validationErrors.slice(0, 20).map((err, i) => (
                      <li key={i} className="text-xs text-red-600 dark:text-red-500">{err}</li>
                    ))}
                    {validationErrors.length > 20 && (
                      <li className="text-xs text-red-400">... és {validationErrors.length - 20} további hiba</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Data preview table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 max-h-64">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      {mappings.filter(m => fieldMapping[m.field]).map(m => (
                        <th key={m.field} className="px-3 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">
                          {m.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {validRows.slice(0, 10).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {mappings.filter(m => fieldMapping[m.field]).map(m => (
                          <td key={m.field} className="px-3 py-2 text-xs text-gray-900 dark:text-gray-300">
                            {String(row[m.field] ?? '').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {validRows.length > 10 && (
                <p className="text-xs text-gray-400 text-center">
                  {tr.import.showingFirst.replace('{count}', String(validRows.length))}
                </p>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('mapping')}>
                  <ArrowLeftIcon className="w-4 h-4 mr-1.5" />
                  {tr.import.back}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={validRows.length === 0}
                >
                  <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
                  {tr.import.importRows.replace('{count}', String(validRows.length))}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 4: Importing */}
          {step === 'importing' && (
            <motion.div
              key="importing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-16"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mb-4"
              />
              <p className="text-lg font-bold text-gray-900 dark:text-white">{tr.import.importing}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{tr.import.importingWait}</p>
            </motion.div>
          )}

          {/* Step 5: Result */}
          {step === 'result' && importResult && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 shadow-lg ${
                    importResult.failed === 0
                      ? 'bg-gradient-to-br from-green-400 to-emerald-500'
                      : 'bg-gradient-to-br from-amber-400 to-orange-500'
                  }`}
                >
                  {importResult.failed === 0 ? (
                    <CheckCircleIcon className="w-10 h-10 text-white" />
                  ) : (
                    <ExclamationTriangleIcon className="w-10 h-10 text-white" />
                  )}
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {importResult.failed === 0 ? tr.import.success : tr.import.completed}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {tr.import.resultSummary.replace('{success}', String(importResult.success)).replace('{failed}', String(importResult.failed))}
                </p>
              </div>

              {importResult.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
                  <p className="text-sm font-bold text-red-700 dark:text-red-400 mb-2">{tr.import.errors}:</p>
                  <ul className="space-y-1">
                    {importResult.errors.slice(0, 30).map((err, i) => (
                      <li key={i} className="text-xs text-red-600 dark:text-red-500">{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-center">
                <Button variant="primary" onClick={handleClose}>
                  <XMarkIcon className="w-4 h-4 mr-1.5" />
                  {tr.import.close}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
};

export default ImportWizard;
